import { arrayOf, shape, string, oneOf, func, bool } from 'prop-types';
import _jsx from '@babel/runtime/helpers/jsx';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { useRef, useEffect, useState, useMemo } from 'react';
import { Switch } from '@andes/switch';
import { a as areEqualObjects, b as buildInitialState, S as SEARCHBOX, d as deserializeState, c as disableSearchbox, g as getDependenciesValues, s as searchboxQueryParams, r as reduceOptions, e as SWITCH, f as enableSwitch, D as DROPDOWN, n as noop, l as labelMatches, h as addQueryParams, i as areValidDependencies, j as isValidSearch, k as isIncludedInOptions } from './helpers-4945f22e.js';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { jsx } from 'react/jsx-runtime';
import { RestClient } from 'frontend-restclient';
({
  renderConfig: arrayOf(shape({
    fieldName: string,
    field: oneOf(["switch", "selector", "searchbox", "not supported"])
  })).isRequired,
  callToAction: string,
  onChange: func,
  onAction: func,
  headers: shape({}),
  secondaryAction: shape({
    label: string,
    onAction: func
  }),
  initialState: shape({})
});
const FacetedSearchDefaultProps = {
  callToAction: null,
  onChange: null,
  onAction: null,
  headers: null,
  secondaryAction: null,
  initialState: null
};

/**
 * useAfterEffect is a useEffect hooks who only triggers after the initial render
 * happens, use it to safely do API calls
 * @param {*} fx The main function as would be used on useEffect callback
 * @param {*} deps The dependencies array as would be used on useEffect callback
 */
const useAfterEffect = (fx, deps) => {
  const didMount = useRef(false);
  useEffect(() => {
    if (didMount.current) {
      fx();
    } else {
      didMount.current = true;
    }
  }, [deps]); // eslint-disable-line
};
var useEffectAfterRender = useAfterEffect;
const SMALL = 'small';
const MEDIUM = 'medium';
const LARGE = 'large';
const XLARGE = 'xlarge';
const useChangeDetection = function (value) {
  let initialValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const cachedValue = useRef(initialValue);
  const changed = !areEqualObjects(value, cachedValue.current);
  useEffect(() => {
    cachedValue.current = value;
  }, [value]);
  return {
    changed
  };
};
const buildFieldFromSpec = _ref => {
  let {
    id,
    type,
    state,
    setState,
    placeholderLabel,
    options,
    enabledAt,
    disableIfEmpty,
    searchParams,
    api,
    label,
    headers,
    dependencies,
    size,
    minSearchLength,
    components: {
      Dropdown,
      Searchbox
    }
  } = _ref;
  let disabled = false;
  const value = state[id];
  switch (type) {
    case DROPDOWN:
      return /*#__PURE__*/_jsx(Dropdown, {
        id: id,
        searchParams: searchParams,
        placeholderLabel: placeholderLabel,
        options: reduceOptions(id, options, state),
        value: value,
        setValue: newValue => setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
          [id]: newValue
        })),
        doApiCall: false,
        api: api,
        headers: headers,
        disableIfEmpty: disableIfEmpty,
        size: size
      }, id);
    case SWITCH:
      disabled = !enableSwitch(id, state, setState, enabledAt);
      return /*#__PURE__*/_jsx(Switch, {
        className: "faceted-search-switch",
        label: /*#__PURE__*/_jsx("span", {
          className: "faceted-search-switch__label"
        }, void 0, label),
        inputProps: {
          name: 'my-switch',
          autoComplete: 'off',
          value,
          headers
        },
        checked: disabled ? false : value,
        onChange: () => setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
          [id]: !prevState[id]
        })),
        disabled: disabled
      }, id);
    case SEARCHBOX:
      const isSearchbox = !dependencies && !!api;
      const Selector = isSearchbox ? Searchbox : Dropdown;
      const searchState = deserializeState(state);
      return /*#__PURE__*/_jsx(Selector, {
        id: id,
        value: value,
        setValue: newValue => setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
          [id]: newValue
        })),
        placeholderLabel: placeholderLabel,
        disabled: disableSearchbox(dependencies, state),
        dependencies: getDependenciesValues(state, dependencies),
        withSearchBox: true,
        searchParams: searchboxQueryParams(dependencies, state),
        options: reduceOptions(id, options, state),
        optionsReducer: nextOptions => reduceOptions(id, nextOptions, state),
        api: api,
        headers: headers,
        disableIfEmpty: disableIfEmpty,
        size: size,
        minSearchLength: minSearchLength,
        searchState: searchState
      }, id);
    default:
      return null;
  }
};
const isFunction = fn => !!(fn && typeof fn === "function");
const useFacetedSearch = _ref2 => {
  let {
    renderConfig,
    onChange,
    headers,
    initialState,
    components
  } = _ref2;
  const [state, setState] = useState(() => buildInitialState(renderConfig, initialState));
  const {
    changed
  } = useChangeDetection(state, state);
  useEffect(() => {
    if (initialState) {
      setState(buildInitialState(renderConfig, initialState));
    }
  }, [initialState]);
  useEffectAfterRender(() => {
    if (changed && isFunction(onChange)) {
      onChange(state);
    }
  }, [state, onChange, changed]);
  const elements = renderConfig.map(field => buildFieldFromSpec(_objectSpread(_objectSpread({}, field), {}, {
    headers,
    state,
    setState,
    components
  })));
  return [elements, state];
};
buildFieldFromSpec.defaultProps = {
  placeholderLabel: "",
  options: [],
  enabledAt: {},
  disableIfEmpty: false,
  searchParams: "",
  api: null,
  label: null,
  headers: null,
  dependencies: [],
  size: 'medium',
  minSearchLength: 1
};
var useFacetedSearch$1 = useFacetedSearch;
const OPTION_SHAPE = {
  id: string.isRequired,
  label: string.isRequired,
  hiddenAt: shape({})
};
const DROPDOWN_BASE_PROP_TYPES = {
  disableIfEmpty: bool,
  disabled: bool,
  placeholderLabel: string.isRequired,
  setValue: func.isRequired,
  size: oneOf([SMALL, MEDIUM, LARGE, XLARGE]),
  value: string,
  withSearchBox: bool
};
const DROPDOWN_BASE_DEFAULT_PROPS = {
  disabled: false,
  disableIfEmpty: false,
  size: MEDIUM,
  value: null,
  withSearchBox: false
};
const DROPDOWN_CONTAINER_PROP_TYPES = _objectSpread(_objectSpread({}, DROPDOWN_BASE_PROP_TYPES), {}, {
  id: string.isRequired,
  searchParams: string,
  dependencies: shape({}),
  options: arrayOf(shape(OPTION_SHAPE)),
  api: string,
  optionsReducer: func,
  headers: shape({})
});
const DROPDOWN_CONTAINER_DEFAULT_PROPS = _objectSpread(_objectSpread({}, DROPDOWN_BASE_DEFAULT_PROPS), {}, {
  searchParams: "",
  dependencies: null,
  options: [],
  api: null,
  optionsReducer: noop,
  headers: {}
});
_objectSpread(_objectSpread({}, DROPDOWN_BASE_PROP_TYPES), {}, {
  availableOptions: arrayOf(shape(OPTION_SHAPE)),
  searchTerm: string,
  setSearchTerm: func,
  setShowSuggester: func,
  showSuggester: bool
});
const DROPDOWN_COMPONENT_DEFAULT_PROPS = _objectSpread(_objectSpread({}, DROPDOWN_BASE_DEFAULT_PROPS), {}, {
  availableOptions: [],
  searchTerm: "",
  setSearchTerm: noop,
  setShowSuggester: noop,
  showSuggester: false
});
const useVisibleOptions = (source, searchTerm) => useMemo(() => searchTerm.trim() === "" ? source : source.filter(labelMatches(searchTerm)), [source, searchTerm]);
const _excluded$3 = ["options"];
const withClientSideSearch = SearchableComponent => _ref => {
  let {
      options
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded$3);
  const [showSuggester, setShowSuggester] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const visibleOptions = useVisibleOptions(options, searchTerm);
  return /*#__PURE__*/jsx(SearchableComponent, _objectSpread(_objectSpread({}, otherProps), {}, {
    showSuggester: showSuggester,
    setShowSuggester: setShowSuggester,
    availableOptions: visibleOptions,
    searchTerm: searchTerm,
    setSearchTerm: setSearchTerm
  }));
};
const _excluded$2 = ["options"];
const withDefaultOptions = NonSearchableComponent => _ref => {
  let {
      options
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded$2);
  const [showSuggester, setShowSuggester] = useState(false);
  return /*#__PURE__*/jsx(NonSearchableComponent, _objectSpread(_objectSpread({}, otherProps), {}, {
    availableOptions: options,
    showSuggester: showSuggester,
    setShowSuggester: setShowSuggester
  }));
};
const DEFAULT_DEBOUNCE_IN_MS = 500;
const useDebouncedValue = function (value) {
  let ms = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_DEBOUNCE_IN_MS;
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const currentTimeout = setTimeout(() => {
      setDebouncedValue(value);
    }, ms);
    return () => {
      clearTimeout(currentTimeout);
    };
  }, [value, ms]);
  return debouncedValue;
};
const _excluded$1 = ["api", "dependencies", "headers", "id", "options", "optionsReducer", "searchParams", "minSearchLength"];
const restclient$1 = RestClient({
  timeout: 2000
});
const withDependencyChangeSearch = SearchableComponent => _ref => {
  let {
      api,
      dependencies,
      headers,
      id,
      options,
      optionsReducer,
      searchParams,
      minSearchLength
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded$1);
  const [showSuggester, setShowSuggester] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [availableOptions, setAvailableOptions] = useState(options);
  const {
    changed
  } = useChangeDetection(dependencies);
  const visibleOptions = useVisibleOptions(availableOptions, searchTerm);
  const debouncedSearch = useDebouncedValue(searchTerm);
  const requestUrl = addQueryParams(api, "".concat(id, "=").concat(debouncedSearch, "&").concat(searchParams));
  useEffect(() => {
    const validChange = changed && areValidDependencies(dependencies);
    if (validChange) {
      setSearchTerm("");
      setAvailableOptions([]);
    }
    if (validChange || isValidSearch(debouncedSearch, minSearchLength)) {
      restclient$1.get(requestUrl, {
        withCredentials: true,
        headers
      }).then(_ref2 => {
        let {
          data
        } = _ref2;
        setAvailableOptions(optionsReducer(data));
      }).catch(noop);
    }
  }, [changed, dependencies, requestUrl]);
  return /*#__PURE__*/jsx(SearchableComponent, _objectSpread(_objectSpread({}, otherProps), {}, {
    id: id,
    showSuggester: showSuggester,
    setShowSuggester: setShowSuggester,
    availableOptions: visibleOptions,
    searchTerm: searchTerm,
    setSearchTerm: setSearchTerm
  }));
};
const _excluded = ["api", "headers", "id", "options", "optionsReducer", "searchParams", "minSearchLength", "value"];
const restclient = RestClient({
  timeout: 2000
});
const getInititalSearchTerm = value => {
  const {
    label = ''
  } = deserializeState({
    value
  });
  return label;
};
const withServerSideSearch = SearchableComponent => _ref => {
  let {
      api,
      headers,
      id,
      options,
      optionsReducer,
      searchParams,
      minSearchLength,
      value
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const [hasChanged, setHasChanged] = useState(false);
  const [showSuggester, setShowSuggester] = useState(false);
  const [searchTerm, setSearchTerm] = useState(getInititalSearchTerm(value));
  const [updateResults, setUpdateResults] = useState(true);
  const [availableOptions, setAvailableOptions] = useState(options);
  const debouncedSearch = useDebouncedValue(searchTerm);
  const requestUrl = isValidSearch(debouncedSearch, minSearchLength) ? addQueryParams(api, "".concat(id, "=").concat(debouncedSearch, "&").concat(searchParams)) : null;
  useEffect(() => {
    if (!requestUrl || !updateResults) {
      return;
    }
    restclient.get(requestUrl, {
      withCredentials: true,
      headers
    }).then(_ref2 => {
      let {
        data
      } = _ref2;
      setAvailableOptions(optionsReducer(data));
    }).catch(noop);
  }, [requestUrl]);
  return /*#__PURE__*/jsx(SearchableComponent, _objectSpread(_objectSpread({}, otherProps), {}, {
    id: id,
    value: value,
    showSuggester: showSuggester && hasChanged,
    setShowSuggester: setShowSuggester,
    availableOptions: availableOptions,
    searchTerm: searchTerm,
    setSearchTerm: function (term) {
      let fetchNewResults = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      setSearchTerm(term);
      setUpdateResults(fetchNewResults);
      setHasChanged(true);
    }
  }));
};
const DROPDOWN_TYPES = {
  CLIENT_SIDE: "client_side",
  DEPENDENCY: "dependency",
  SERVER_SIDE: "server_side",
  DEFAULT: "default"
};
const isClientSideSearch = _ref => {
  let {
    api,
    withSearchBox
  } = _ref;
  return !api && withSearchBox;
};
const isDependencyChangeSearch = _ref2 => {
  let {
    api,
    dependencies,
    withSearchBox
  } = _ref2;
  return !!(api && dependencies && withSearchBox);
};
const isServerSideSearch = _ref3 => {
  let {
    api,
    dependencies,
    withSearchBox
  } = _ref3;
  return !!(api && !dependencies && withSearchBox);
};
const createDropdownSelector = DropdownComponent => {
  const dropdownComponentsByType = {
    [DROPDOWN_TYPES.CLIENT_SIDE]: withClientSideSearch(DropdownComponent),
    [DROPDOWN_TYPES.DEPENDENCY]: withDependencyChangeSearch(DropdownComponent),
    [DROPDOWN_TYPES.SERVER_SIDE]: withServerSideSearch(DropdownComponent),
    [DROPDOWN_TYPES.DEFAULT]: withDefaultOptions(DropdownComponent)
  };
  const DropdownSelector = props => {
    if (isClientSideSearch(props)) {
      const Dropdown = dropdownComponentsByType[DROPDOWN_TYPES.CLIENT_SIDE];
      return /*#__PURE__*/jsx(Dropdown, _objectSpread({}, props));
    }
    if (isDependencyChangeSearch(props)) {
      const Dropdown = dropdownComponentsByType[DROPDOWN_TYPES.DEPENDENCY];
      return /*#__PURE__*/jsx(Dropdown, _objectSpread({}, props));
    }
    if (isServerSideSearch(props)) {
      const Dropdown = dropdownComponentsByType[DROPDOWN_TYPES.SERVER_SIDE];
      return /*#__PURE__*/jsx(Dropdown, _objectSpread({}, props));
    }
    const DefaultDropdown = dropdownComponentsByType[DROPDOWN_TYPES.DEFAULT];
    return /*#__PURE__*/jsx(DefaultDropdown, _objectSpread({}, props));
  };
  return DropdownSelector;
};
const useValueReset = _ref => {
  let {
    availableOptions,
    value,
    setValue,
    disabled
  } = _ref;
  useEffect(() => {
    if (value && (disabled || !isIncludedInOptions(availableOptions, value))) {
      setValue(null);
    }
  }, [disabled, availableOptions]);
};
const namespace = 'faceted-search-highlighted-text';
const getTextParts = _ref => {
  let {
    text,
    highlightTerm
  } = _ref;
  const splitRegex = new RegExp("(".concat(highlightTerm, ")"), 'i');
  return text.split(splitRegex).filter(Boolean);
};
const HighlightedText = _ref2 => {
  let {
    text,
    highlightTerm: rawTerm,
    testId
  } = _ref2;
  const renderPart = _ref3 => {
    let {
      part,
      highlightTerm
    } = _ref3;
    const isHightlighted = part.match(new RegExp(highlightTerm, 'i'));
    return isHightlighted ? /*#__PURE__*/_jsx("b", {
      "data-testid": "".concat(testId, "-bold")
    }, void 0, part) : part;
  };

  // Here we need to escape every RexExp special character
  // Recommended by https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
  const highlightTerm = rawTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const resultText = getTextParts({
    text,
    highlightTerm
  });
  return /*#__PURE__*/_jsx("div", {
    "data-testid": testId,
    className: namespace
  }, void 0, resultText.map(part => renderPart({
    part,
    highlightTerm
  })));
};
const useHighlightTerm = (searchTerm, availableOptions) => useMemo(() => searchTerm, [availableOptions]);
export { DROPDOWN_CONTAINER_PROP_TYPES as D, FacetedSearchDefaultProps as F, HighlightedText as H, DROPDOWN_CONTAINER_DEFAULT_PROPS as a, DROPDOWN_COMPONENT_DEFAULT_PROPS as b, createDropdownSelector as c, useHighlightTerm as d, useFacetedSearch$1 as e, useChangeDetection as f, useValueReset as u };
